// src/components/SolicitationDocumentsStep.tsx
// Document attachment step before proposal generation
// Allows users to attach solicitation documents (RFP, SOW, etc.) for requirements extraction

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X,
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Info,
  Download,
  FileWarning,
  Sparkles,
  Clock,
  FileCheck,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Contract } from '../types';
import { supabase } from '../lib/supabase';
import { useContractDocuments, ContractDocument } from '../hooks/useContractDocuments';
import { DocumentAnalyzer } from '../lib/document-analysis';
import toast from 'react-hot-toast';

interface SolicitationDocumentsStepProps {
  contract: Contract;
  proposalType: 'basic' | 'enhanced' | 'source-grounded';
  onBack: () => void;
  onContinue: (documentsReady: boolean) => void;
  onSkip: () => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'extracting' | 'processing' | 'completed' | 'failed';
  error?: string;
  extractedText?: string;
  wordCount?: number;
}

export function SolicitationDocumentsStep({
  contract,
  proposalType,
  onBack,
  onContinue,
  onSkip,
}: SolicitationDocumentsStepProps) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingDocuments, setProcessingDocuments] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [fetchingSam, setFetchingSam] = useState(false);
  const autoFetchRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const noticeId = contract.noticeId || contract.id;
  // Include extracted text to show summaries
  const { documents, loading, refresh, isReadyForProposal, getDocumentsSummary } = useContractDocuments({
    noticeId,
    includeExtractedText: true,
  });

  // SAM.gov document links can live in either the top-level resource_links
  // column or inside metadata.resource_links, and each entry may be a plain URL
  // string or an object. Normalize both shapes so the step always sees them.
  const rawSamLinks: any[] =
    (Array.isArray(contract.resourceLinks) && contract.resourceLinks.length > 0
      ? contract.resourceLinks
      : (contract as any).metadata?.resource_links) || [];
  const samDocuments = rawSamLinks
    .map((d: any, i: number) =>
      typeof d === 'string'
        ? { url: d, title: `Document ${i + 1}`, name: `Document ${i + 1}` }
        : { url: d.url || d.link || d.accessUrl, title: d.title || d.name || `Document ${i + 1}`, name: d.name }
    )
    .filter((d: any) => d.url);
  const samDocumentCount = samDocuments.length;

  // Check if we have processed documents ready
  const processedDocuments = documents.filter(d => d.status === 'completed');
  const pendingDocuments = documents.filter(d => d.status === 'pending' || d.status === 'processing');
  const failedDocuments = documents.filter(d => d.status === 'failed');

  // Calculate overall extraction progress
  const totalDocs = documents.length;
  const extractionProgress = totalDocs > 0 ? Math.round((processedDocuments.length / totalDocs) * 100) : 0;
  const allExtracted = pendingDocuments.length === 0 && documents.length > 0;
  const hasAnyExtracted = processedDocuments.length > 0;

  // Poll for document status updates when there are pending documents
  useEffect(() => {
    // Start polling if we have pending documents
    if (pendingDocuments.length > 0 && !pollingIntervalRef.current) {
      console.log('📡 Starting document status polling...');
      setIsPolling(true);

      pollingIntervalRef.current = setInterval(() => {
        console.log('🔄 Polling for document status updates...');
        refresh();
      }, 2000); // Poll every 2 seconds
    }

    // Stop polling if all documents are processed
    if (pendingDocuments.length === 0 && pollingIntervalRef.current) {
      console.log('✅ All documents processed, stopping polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pendingDocuments.length, refresh]);

  // Pull the agency's documents straight from SAM.gov into contract_documents
  // (download + extract text + index) so the proposal generator has real
  // solicitation text to work with. Older contracts were never auto-fetched, so
  // this fills the gap on demand.
  const handleFetchFromSam = useCallback(async () => {
    if (fetchingSam) return;
    setFetchingSam(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-sam-documents', {
        body: { contract_id: noticeId },
      });
      if (error) throw error;
      const downloaded = typeof data?.downloaded === 'number'
        ? data.downloaded
        : (Array.isArray(data?.files) ? data.files.filter((r: any) => r.status === 'downloaded').length : 0);
      if (downloaded > 0) {
        toast.success(`Imported ${downloaded} document${downloaded !== 1 ? 's' : ''} from SAM.gov`);
      } else {
        toast('No downloadable documents were found on SAM.gov for this notice. You can upload them manually.', { icon: 'ℹ️' });
      }
      await refresh();
    } catch (e) {
      console.error('SAM fetch error:', e);
      toast.error('Could not import documents from SAM.gov. You can upload them manually below.');
    } finally {
      setFetchingSam(false);
    }
  }, [fetchingSam, noticeId, refresh]);

  // Auto-import from SAM.gov once when the step opens with links available but
  // nothing downloaded yet — so the documents "come over" without extra clicks.
  useEffect(() => {
    if (!loading && documents.length === 0 && samDocumentCount > 0 && !autoFetchRef.current) {
      autoFetchRef.current = true;
      handleFetchFromSam();
    }
  }, [loading, documents.length, samDocumentCount, handleFetchFromSam]);

  // Get a text preview for a document (first 500 chars)
  const getTextPreview = (doc: ContractDocument): string => {
    if (!doc.raw_text) return '';
    // Clean up the text and get first 500 chars
    const cleaned = doc.raw_text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= 500) return cleaned;
    return cleaned.substring(0, 500) + '...';
  };

  // Get extraction stats for a document
  const getExtractionStats = (doc: ContractDocument) => {
    if (!doc.raw_text) {
      return { charCount: 0, wordCount: 0, lineCount: 0 };
    }
    const text = doc.raw_text;
    const charCount = text.length;
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const lineCount = text.split('\n').filter(Boolean).length;
    return { charCount, wordCount, lineCount };
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function for timeouts
  const withExtractionTimeout = <T,>(promise: Promise<T>, timeoutMs: number, fileName: string): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Text extraction timed out for ${fileName}. Try a smaller file or contact support.`)), timeoutMs)
      )
    ]);
  };

  // Client-side text extraction (FAST - no server round trip)
  const extractTextClientSide = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    console.log(`📄 [${file.name}] Starting client-side extraction (type: ${file.type}, ext: ${ext})`);

    // Maximum extraction time: 2 minutes for PDFs, 1 minute for others
    const isPdf = file.type === 'application/pdf' || ext === 'pdf';
    const maxExtractionTime = isPdf ? 120000 : 60000;

    try {
      // Wrap entire extraction in a timeout to prevent indefinite hanging
      const extractionPromise = (async () => {
        // Use DocumentAnalyzer for supported types (mammoth for docx, xlsx for excel, pdfjs for pdf)
        if (isPdf) {
          return await DocumentAnalyzer.processFile(file);
        }

        if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            file.type === 'application/msword' || ext === 'docx' || ext === 'doc') {
          return await DocumentAnalyzer.processFile(file);
        }

        if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' || ext === 'xlsx' || ext === 'xls') {
          return await DocumentAnalyzer.processFile(file);
        }

        // Plain text files - read directly
        if (file.type === 'text/plain' || file.type === 'text/csv' || ext === 'txt' || ext === 'csv') {
          return await file.text();
        }

        // Fallback - try DocumentAnalyzer
        return await DocumentAnalyzer.processFile(file);
      })();

      return await withExtractionTimeout(extractionPromise, maxExtractionTime, file.name);
    } catch (error: any) {
      console.error(`Client-side extraction failed for ${file.name}:`, error.message || error);
      throw error;
    }
  };

  // Process a single file (extract + upload + save)
  const processFile = async (
    file: File,
    fileIndex: number,
    updateProgress: (index: number, update: Partial<UploadingFile>) => void
  ): Promise<void> => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    try {
      // Step 1: Check authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Not authenticated');

      // Step 2: Check for duplicates
      const { data: existingDoc } = await supabase
        .from('contract_documents')
        .select('id, status')
        .eq('contract_id', noticeId)
        .eq('document_name', file.name)
        .maybeSingle();

      if (existingDoc) {
        throw new Error(`Already uploaded (${existingDoc.status === 'completed' ? 'ready' : existingDoc.status})`);
      }

      // Step 3: Extract text CLIENT-SIDE (this is FAST!)
      console.log(`📝 [${file.name}] Extracting text client-side...`);
      updateProgress(fileIndex, { status: 'extracting', progress: 10 });

      let extractedText = '';
      let pageCount = 1;

      try {
        extractedText = await extractTextClientSide(file);
        // Estimate page count (rough: ~3000 chars per page)
        pageCount = Math.max(1, Math.ceil(extractedText.length / 3000));
        console.log(`✓ [${file.name}] Extracted ${extractedText.length} chars (${pageCount} pages)`);
      } catch (extractError) {
        console.warn(`⚠️ [${file.name}] Client extraction failed, will save without text:`, extractError);
        // Continue without extracted text - better than failing completely
      }

      const wordCount = extractedText.split(/\s+/).filter(Boolean).length;
      updateProgress(fileIndex, {
        progress: 40,
        extractedText: extractedText.substring(0, 500),
        wordCount
      });

      // Step 4: Upload file to storage
      console.log(`📦 [${file.name}] Uploading to storage...`);
      updateProgress(fileIndex, { status: 'uploading', progress: 50 });

      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
      const filePath = `solicitation-documents/${noticeId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      updateProgress(fileIndex, { progress: 70 });

      // Step 5: Save to database
      console.log(`💾 [${file.name}] Saving to database...`);
      updateProgress(fileIndex, { status: 'processing', progress: 80 });

      // If we have extracted text, save it directly as completed
      // Otherwise, save as pending and trigger server-side extraction
      const hasExtractedText = extractedText && extractedText.length > 50;

      const { data: insertedDoc, error: dbError } = await supabase
        .from('contract_documents')
        .insert({
          contract_id: noticeId,
          document_name: file.name,
          document_url: urlData.publicUrl,
          document_type: 'attachment',
          file_type: ext,
          file_size: file.size,
          raw_text: extractedText || null,
          page_count: pageCount,
          status: hasExtractedText ? 'completed' : 'pending',
          processed_at: hasExtractedText ? new Date().toISOString() : null,
        })
        .select('id')
        .single();

      if (dbError) {
        if (dbError.code === '23505') throw new Error('Document already exists');
        throw dbError;
      }

      // Step 6: If client-side extraction failed, trigger server-side extraction
      if (!hasExtractedText && insertedDoc?.id) {
        console.log(`🔄 [${file.name}] Client extraction failed, triggering server-side extraction...`);
        updateProgress(fileIndex, { status: 'extracting', progress: 85 });

        try {
          const { error: extractError } = await supabase.functions.invoke('extract-document-text', {
            body: { documentId: insertedDoc.id }
          });

          if (extractError) {
            console.warn(`⚠️ [${file.name}] Server extraction failed:`, extractError);
            // Don't throw - document is saved, extraction can be retried
          } else {
            console.log(`✓ [${file.name}] Server extraction triggered successfully`);
          }
        } catch (serverError) {
          console.warn(`⚠️ [${file.name}] Server extraction error:`, serverError);
          // Don't throw - let the polling handle the status update
        }
      }

      // Done!
      updateProgress(fileIndex, { status: 'completed', progress: 100 });
      console.log(`✅ [${file.name}] Complete! ${wordCount} words extracted`);

    } catch (error) {
      console.error(`❌ [${file.name}] Failed:`, error);
      updateProgress(fileIndex, {
        status: 'failed',
        error: (error as Error).message
      });
      throw error;
    }
  };

  // File upload handler - processes ALL files IN PARALLEL
  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
    ];
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];

    const validFiles = fileArray.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      if (!allowedTypes.includes(f.type) && !allowedExtensions.includes(ext)) {
        toast.error(`${f.name}: Unsupported file type`);
        return false;
      }
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name}: File too large (max 50MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Add all files to state at once
    const startIndex = uploadingFiles.length;
    const newUploading: UploadingFile[] = validFiles.map(file => ({
      file,
      progress: 0,
      status: 'extracting' as const,
    }));
    setUploadingFiles(prev => [...prev, ...newUploading]);

    // Helper to update a specific file's progress
    const updateProgress = (index: number, update: Partial<UploadingFile>) => {
      setUploadingFiles(prev => prev.map((f, i) =>
        i === startIndex + index ? { ...f, ...update } : f
      ));
    };

    // Process ALL files in PARALLEL (much faster!)
    console.log(`🚀 Processing ${validFiles.length} files in parallel...`);
    const startTime = Date.now();

    const results = await Promise.allSettled(
      validFiles.map((file, index) => processFile(file, index, updateProgress))
    );

    const elapsed = Date.now() - startTime;
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    console.log(`✨ Processed ${validFiles.length} files in ${elapsed}ms (${succeeded} succeeded, ${failed} failed)`);

    if (succeeded > 0) {
      toast.success(`${succeeded} document${succeeded !== 1 ? 's' : ''} ready!`);
    }
    if (failed > 0) {
      toast.error(`${failed} document${failed !== 1 ? 's' : ''} failed`);
    }

    // Refresh and clean up
    refresh();
    setTimeout(() => {
      setUploadingFiles(prev => prev.filter(f => f.status !== 'completed'));
    }, 2000);
  }, [noticeId, uploadingFiles.length, refresh]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  // Delete document
  const handleDeleteDocument = async (doc: ContractDocument) => {
    if (!confirm('Are you sure you want to remove this document?')) return;

    try {
      const { error } = await supabase
        .from('contract_documents')
        .delete()
        .eq('id', doc.id);

      if (error) throw error;
      toast.success('Document removed');
      refresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove document');
    }
  };

  // Process all documents (extract requirements)
  const handleProcessAllDocuments = async () => {
    setProcessingDocuments(true);

    try {
      // If we have processed documents, analyze them for requirements
      if (processedDocuments.length > 0) {
        // Call edge function to analyze documents and extract requirements
        const { error } = await supabase.functions.invoke('analyze-contract-documents', {
          body: {
            contractId: noticeId,
          }
        });

        if (error) {
          console.error('Analysis error:', error);
          // Don't block - continue even if analysis fails
          toast.error('Could not analyze documents, but you can continue.');
        } else {
          toast.success('Documents analyzed! Requirements extracted.');
        }
      }

      onContinue(processedDocuments.length > 0);

    } catch (error) {
      console.error('Requirements extraction error:', error);
      toast.error('Analysis failed. Continuing without document analysis.');
      onContinue(false);
    } finally {
      setProcessingDocuments(false);
    }
  };

  // Determine file type icon
  const getFileIcon = (fileType: string) => {
    if (fileType === 'pdf') return <FileText className="h-5 w-5 text-urgent" />;
    if (fileType === 'docx' || fileType === 'doc') return <FileText className="h-5 w-5 text-bw-primary" />;
    if (fileType === 'xlsx' || fileType === 'xls' || fileType === 'csv') return <FileText className="h-5 w-5 text-success" />;
    return <FileText className="h-5 w-5 text-bw-muted" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-bw-border bg-bw-primary-tint">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-bw-ink">Attach Solicitation Documents</h2>
              <p className="text-sm text-bw-muted mt-1">
                Upload RFP, SOW, or other solicitation documents for accurate proposal generation
              </p>
            </div>
            <button
              onClick={onBack}
              className="p-2 -m-2 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* SAM.gov Documents Alert */}
          {samDocumentCount > 0 && (
            <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-fit-moderate flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-fit-moderate">
                    {samDocumentCount} document{samDocumentCount !== 1 ? 's' : ''} available on SAM.gov
                  </p>
                  <p className="text-sm text-fit-moderate mt-1">
                    Download and attach these documents to ensure your proposal addresses all requirements.
                    These documents contain the actual evaluation criteria the agency will use.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {samDocuments.slice(0, 5).map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-bw-surface border border-amber-300 rounded-lg text-sm text-fit-moderate hover:bg-fit-moderate-bg transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {doc.title || doc.name || `Document ${idx + 1}`}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                    {samDocumentCount > 5 && (
                      <span className="text-sm text-fit-moderate self-center">
                        +{samDocumentCount - 5} more
                      </span>
                    )}
                  </div>
                  {/* One-click import: download + extract these into the proposal */}
                  <button
                    onClick={handleFetchFromSam}
                    disabled={fetchingSam}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-60 transition-colors"
                  >
                    {fetchingSam ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Importing from SAM.gov…</>
                    ) : (
                      <><Download className="h-4 w-4" /> Import {samDocumentCount} document{samDocumentCount !== 1 ? 's' : ''} into this proposal</>
                    )}
                  </button>
                  <p className="text-xs text-fit-moderate mt-1.5">
                    We download these and extract their text automatically so your proposal addresses the real requirements — no manual download needed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Auto-import in progress (no documents yet) */}
          {fetchingSam && documents.length === 0 && (
            <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4 flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
              <div>
                <p className="font-medium text-bw-primary">Importing documents from SAM.gov…</p>
                <p className="text-sm text-bw-primary">Downloading and extracting text. This usually takes 10–60 seconds.</p>
              </div>
            </div>
          )}

          {/* Why This Matters */}
          <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-bw-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-bw-primary">Why attach documents?</p>
                <ul className="text-sm text-bw-primary mt-2 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-bw-primary flex-shrink-0 mt-0.5" />
                    <span>Requirements are extracted and your proposal addresses each one</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-bw-primary flex-shrink-0 mt-0.5" />
                    <span>Gap analysis shows exactly what you need in your profile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-bw-primary flex-shrink-0 mt-0.5" />
                    <span>AI feedback tells you what's missing and how to fix it</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-bw-primary flex-shrink-0 mt-0.5" />
                    <span>Proposal is tailored to evaluation criteria the COR will use</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              isDragOver
                ? 'border-blue-400 bg-bw-primary-tint'
                : 'border-bw-border hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-10 w-10 mx-auto text-bw-muted mb-3" />
            <p className="text-bw-ink font-medium">
              Drag and drop files here, or{' '}
              <label className="text-bw-primary hover:text-bw-primary cursor-pointer">
                browse
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  className="hidden"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                />
              </label>
            </p>
            <p className="text-sm text-bw-muted mt-1">
              PDF, Word, Excel, or text files (max 50MB each)
            </p>
          </div>

          {/* Uploading Files - Now with instant extraction! */}
          {uploadingFiles.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-bw-ink">
                {uploadingFiles.some(f => f.status === 'extracting') ? 'Extracting text...' :
                 uploadingFiles.some(f => f.status === 'uploading') ? 'Uploading...' :
                 uploadingFiles.some(f => f.status === 'processing') ? 'Saving...' : 'Processing...'}
              </h4>
              {uploadingFiles.map((uf, idx) => (
                <div key={idx} className={`flex items-center gap-3 rounded-lg p-3 ${
                  uf.status === 'completed' ? 'bg-success-bg' :
                  uf.status === 'failed' ? 'bg-urgent-bg' : 'bg-bw-surface-subtle'
                }`}>
                  {uf.status === 'extracting' ? (
                    <Loader2 className="h-5 w-5 text-bw-primary animate-spin" />
                  ) : uf.status === 'uploading' || uf.status === 'processing' ? (
                    <Loader2 className="h-5 w-5 text-bw-primary animate-spin" />
                  ) : uf.status === 'completed' ? (
                    <CheckCircle className="h-5 w-5 text-success" />
                  ) : (
                    <FileWarning className="h-5 w-5 text-urgent" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-bw-ink truncate">{uf.file.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {uf.status === 'extracting' && (
                        <span className="text-xs text-bw-primary">Extracting text...</span>
                      )}
                      {uf.status === 'uploading' && (
                        <span className="text-xs text-bw-primary">Uploading...</span>
                      )}
                      {uf.status === 'processing' && (
                        <span className="text-xs text-bw-primary">Saving...</span>
                      )}
                      {uf.status === 'completed' && uf.wordCount && (
                        <span className="text-xs text-success font-medium">
                          {uf.wordCount.toLocaleString()} words extracted
                        </span>
                      )}
                      {uf.status === 'failed' && uf.error && (
                        <span className="text-xs text-urgent">{uf.error}</span>
                      )}
                    </div>
                    {(uf.status === 'extracting' || uf.status === 'uploading' || uf.status === 'processing') && (
                      <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1.5">
                        <div
                          className={`h-full rounded-full transition-all ${
                            uf.status === 'extracting' ? 'bg-bw-primary-tint0' : 'bg-bw-primary-tint0'
                          }`}
                          style={{ width: `${uf.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Overall Extraction Progress */}
          {documents.length > 0 && pendingDocuments.length > 0 && (
            <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="h-5 w-5 text-bw-primary animate-spin" />
                <div className="flex-1">
                  <p className="font-medium text-bw-primary">
                    Extracting text from documents...
                  </p>
                  <p className="text-sm text-bw-primary">
                    {processedDocuments.length} of {totalDocs} documents processed
                  </p>
                </div>
                <span className="text-lg font-bold text-bw-primary">{extractionProgress}%</span>
              </div>
              <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-bw-primary rounded-full transition-all duration-500"
                  style={{ width: `${extractionProgress}%` }}
                />
              </div>
              <p className="text-xs text-bw-primary mt-2">
                Please wait for extraction to complete before continuing. This ensures accurate proposal generation.
              </p>
            </div>
          )}

          {/* Extraction Complete Banner */}
          {allExtracted && processedDocuments.length > 0 && (
            <div className="bg-success-bg border border-success-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FileCheck className="h-5 w-5 text-success" />
                <div className="flex-1">
                  <p className="font-medium text-success">
                    All documents processed successfully!
                  </p>
                  <p className="text-sm text-success">
                    {(() => {
                      const summary = getDocumentsSummary();
                      return `${summary.extractedDocuments} documents • ${summary.totalCharacters.toLocaleString()} characters extracted`;
                    })()}
                  </p>
                </div>
                <CheckCircle className="h-6 w-6 text-success" />
              </div>
            </div>
          )}

          {/* Attached Documents with Extraction Details */}
          {documents.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-bw-ink">
                Attached Documents ({documents.length})
              </h4>
              <div className="space-y-2">
                {documents.map((doc) => {
                  const stats = getExtractionStats(doc);
                  const isExpanded = expandedDocId === doc.id;
                  const preview = getTextPreview(doc);

                  return (
                    <div
                      key={doc.id}
                      className={`bg-bw-surface-subtle rounded-lg overflow-hidden border ${
                        doc.status === 'completed' ? 'border-green-200' :
                        doc.status === 'failed' ? 'border-red-200' :
                        'border-bw-border'
                      }`}
                    >
                      {/* Document Header */}
                      <div className="flex items-center gap-3 p-3">
                        {doc.status === 'processing' || doc.status === 'pending' ? (
                          <Loader2 className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
                        ) : doc.status === 'completed' ? (
                          getFileIcon(doc.file_type)
                        ) : (
                          <AlertCircle className="h-5 w-5 text-urgent flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-bw-ink truncate">
                            {doc.document_name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              doc.status === 'completed'
                                ? 'bg-success-bg text-success'
                                : doc.status === 'processing'
                                ? 'bg-bw-primary-tint text-bw-primary'
                                : doc.status === 'failed'
                                ? 'bg-urgent-bg text-urgent'
                                : 'bg-fit-moderate-bg text-fit-moderate'
                            }`}>
                              {doc.status === 'completed' ? 'Ready' :
                               doc.status === 'processing' ? 'Extracting...' :
                               doc.status === 'failed' ? 'Failed' : 'Queued'}
                            </span>
                            <span className="text-xs text-bw-muted">
                              {formatFileSize(doc.file_size)}
                            </span>
                            {doc.page_count && (
                              <span className="text-xs text-bw-muted">
                                • {doc.page_count} pages
                              </span>
                            )}
                            {doc.status === 'completed' && stats.charCount > 0 && (
                              <span className="text-xs text-success font-medium">
                                • {stats.wordCount.toLocaleString()} words extracted
                              </span>
                            )}
                          </div>
                          {doc.status === 'failed' && doc.error_message && (
                            <p className="text-xs text-urgent mt-1">{doc.error_message}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.status === 'completed' && doc.raw_text && (
                            <button
                              onClick={() => setExpandedDocId(isExpanded ? null : doc.id)}
                              className="p-1.5 text-bw-muted hover:text-bw-primary hover:bg-bw-primary-tint rounded transition-colors"
                              title={isExpanded ? 'Hide preview' : 'Show extracted text preview'}
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteDocument(doc)}
                            className="p-1.5 text-bw-muted hover:text-urgent hover:bg-urgent-bg rounded transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Expandable Text Preview */}
                      {isExpanded && doc.status === 'completed' && preview && (
                        <div className="border-t border-bw-border p-3 bg-bw-surface">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-4 w-4 text-bw-muted" />
                            <span className="text-xs font-medium text-bw-muted">Extracted Text Preview</span>
                            <span className="text-xs text-bw-muted">
                              ({stats.charCount.toLocaleString()} chars • {stats.wordCount.toLocaleString()} words • {stats.lineCount} lines)
                            </span>
                          </div>
                          <div className="text-xs text-bw-muted bg-bw-surface-subtle rounded p-3 max-h-40 overflow-y-auto font-mono whitespace-pre-wrap">
                            {preview}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status Summary */}
          {documents.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-success">{processedDocuments.length}</p>
                  <p className="text-xs text-bw-muted">Ready</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-bw-primary">{pendingDocuments.length}</p>
                  <p className="text-xs text-bw-muted">Processing</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-urgent">{failedDocuments.length}</p>
                  <p className="text-xs text-bw-muted">Failed</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-bw-border bg-bw-surface-subtle">
          {/* Warning when documents are still processing */}
          {pendingDocuments.length > 0 && documents.length > 0 && (
            <div className="flex items-center gap-2 text-fit-moderate text-sm mb-3 bg-fit-moderate-bg px-3 py-2 rounded-lg">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>
                Waiting for {pendingDocuments.length} document{pendingDocuments.length !== 1 ? 's' : ''} to finish extracting...
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-bw-muted hover:text-bw-ink transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={onSkip}
                className="px-4 py-2 text-bw-muted hover:text-bw-ink transition-colors"
              >
                Skip for now
              </button>
              <button
                onClick={handleProcessAllDocuments}
                disabled={processingDocuments || loading || fetchingSam || (pendingDocuments.length > 0 && documents.length > 0)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg transition-colors ${
                  pendingDocuments.length > 0 && documents.length > 0
                    ? 'bg-gray-300 text-bw-muted cursor-not-allowed'
                    : 'bg-bw-primary text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                title={pendingDocuments.length > 0 ? 'Please wait for all documents to finish extracting' : ''}
              >
                {processingDocuments ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : pendingDocuments.length > 0 && documents.length > 0 ? (
                  <>
                    <Clock className="h-4 w-4" />
                    Extracting ({extractionProgress}%)
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
